import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  UserPlus, 
  Edit, 
  Eye, 
  Search, 
  Filter,
  Plus,
  Save,
  X,
  Lock,
  Unlock,
  Users,
  CheckCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import roleApi from '../../services/admin/roleApi';

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({
    name: ''
  });



  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await roleApi.getAllRoles(token);
      // Backend trả về { success: true, data: roles }
      const rolesData = response.data || [];
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      console.log('Fetched roles:', rolesData);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Có lỗi xảy ra khi tải danh sách roles');
      setRoles([]); // Set empty array khi có lỗi
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!newRole.name.trim()) {
      toast.error('Vui lòng nhập tên vai trò');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const roleData = {
        name: newRole.name
      };
     
      
      const response = await roleApi.createRole(token, roleData);
      setShowAddModal(false);
      setNewRole({ name: '' });
      toast.success('Thêm vai trò thành công!');
      
      // Refresh danh sách roles
      fetchRoles();
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error('Có lỗi xảy ra khi thêm vai trò');
    }
  };

  const handleEditRole = async () => {
    if (!editingRole.name.trim()) {
      toast.error('Tên role không được để trống');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const roleData = {
        name: editingRole.name
        // Tạm thời bỏ description và status vì backend chưa hỗ trợ
      };
      
      await roleApi.updateRole(token, editingRole.id, roleData);
      setShowEditModal(false);
      setEditingRole(null);
      toast.success('Cập nhật role thành công');
      
      // Refresh danh sách roles
      fetchRoles();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Có lỗi xảy ra khi cập nhật role');
    }
  };

  const handleToggleRoleStatus = async (roleId) => {
    try {
      const token = sessionStorage.getItem('token');
      const role = Array.isArray(roles) ? roles.find(r => r.id === roleId) : null;
      if (!role) {
        toast.error('Không tìm thấy role');
        return;
      }
      const newStatus = role.status === 'active' ? 'inactive' : 'active';
      
      const roleData = {
        status: newStatus
      };
      
      await roleApi.updateRole(token, roleId, roleData);
      toast.success('Cập nhật trạng thái role thành công');
      
      // Refresh danh sách roles
      fetchRoles();
    } catch (error) {
      console.error('Error updating role status:', error);
      toast.error('Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };



  const handleViewRole = (role) => {
    setSelectedRole(role);
    setShowDetailModal(true);
  };

  const handleEditRoleClick = (role) => {
    setEditingRole({ 
      ...role, 
      status: role.status || 'active', // Mặc định là active nếu không có status
      description: role.description || '' // Mặc định là empty string nếu không có description
    });
    setShowEditModal(true);
  };



  const filteredRoles = Array.isArray(roles) ? roles.filter(role => {
    const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Tạm thời bỏ qua description và status filter vì backend chưa hỗ trợ
    return matchesSearch;
  }) : [];

  console.log('Roles state:', roles);
  console.log('Filtered roles:', filteredRoles);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Hoạt động';
      case 'inactive':
        return 'Đã khóa';
      default:
        return 'Không xác định';
    }
  };

  return (
    <div className="space-y-6">
             {/* Header */}
       <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
         <div>
           <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Quản lý Role</h1>
           <p className="text-sm sm:text-base text-gray-600 mt-1">Quản lý vai trò và quyền hạn trong hệ thống</p>
         </div>
         
         <button
           onClick={() => setShowAddModal(true)}
           className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
         >
           <Plus className="w-5 h-5" />
           <span>Thêm vai trò</span>
         </button>
       </div>

      

      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border">
        <div className="flex flex-col lg:flex-row items-center space-y-3 sm:space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 w-full lg:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Tìm kiếm role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {/* Tạm thời ẩn status filter vì backend chưa có trường status */}
          {/* <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="active">Hoạt động</option>
              <option value="inactive">Đã khóa</option>
            </select>
          </div> */}
        </div>
      </div>

      {/* Roles Table */}
      <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Danh sách Role</h3>
        </div>
        
        {loading ? (
          <div className="p-6 sm:p-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm sm:text-base text-gray-600 mt-2">Đang tải...</p>
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm sm:text-base text-gray-600">Không tìm thấy role nào</p>
          </div>
        ) : (
          <>
                         {/* Mobile Card View */}
             <div className="lg:hidden space-y-3 p-3 sm:p-4">
               {filteredRoles.map((role) => (
                 <div key={role.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                   <div className="flex items-center justify-between mb-3">
                     <div className="flex items-center">
                       <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                         <Shield className="w-4 h-4 text-blue-600" />
                       </div>
                       <div>
                         <div className="text-sm font-medium text-gray-900">{role.name}</div>
                         <div className="text-xs text-gray-500">ID: {role.id}</div>
                       </div>
                     </div>
                     <div className="flex items-center space-x-2">
                       <button
                         onClick={() => handleViewRole(role)}
                         className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                         title="Xem chi tiết"
                       >
                         <Eye className="w-4 h-4" />
                       </button>
                       <button
                         onClick={() => handleEditRoleClick(role)}
                         className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                         title="Sửa role"
                       >
                         <Edit className="w-4 h-4" />
                       </button>
                       {/* Tạm thời ẩn status toggle vì backend chưa có trường status */}
                       {/* <button
                         onClick={() => handleToggleRoleStatus(role.id)}
                         className={`p-1 transition-colors ${
                           role.status === 'active' 
                             ? 'text-gray-400 hover:text-red-600' 
                             : 'text-gray-400 hover:text-green-600'
                         }`}
                         title={role.status === 'active' ? 'Khóa role' : 'Mở khóa role'}
                       >
                         {role.status === 'active' ? (
                           <Lock className="w-4 h-4" />
                         ) : (
                           <Unlock className="w-4 h-4" />
                         )}
                       </button> */}
                     </div>
                   </div>
                   
                   <div className="grid grid-cols-2 gap-3">
                                           <div>
                        <span className="text-xs text-gray-500">Ngày tạo</span>
                        <div className="text-xs text-gray-900 mt-1">
                          {role.created_at ? new Date(role.created_at).toLocaleDateString('vi-VN') : 'Không có'}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Ngày cập nhật</span>
                        <div className="text-xs text-gray-900 mt-1">
                          {role.updated_at ? new Date(role.updated_at).toLocaleDateString('vi-VN') : 'Không có'}
                        </div>
                      </div>
                   </div>
                 </div>
               ))}
             </div>

                         {/* Desktop Table View */}
             <div className="hidden lg:block overflow-x-auto">
               <table className="w-full">
                 <thead className="bg-gray-50">
                   <tr>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       ID
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Tên Role
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Ngày tạo
                     </th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Ngày cập nhật
                     </th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                       Thao tác
                     </th>
                   </tr>
                 </thead>
                 <tbody className="bg-white divide-y divide-gray-200">
                   {filteredRoles.map((role) => (
                     <tr key={role.id} className="hover:bg-gray-50">
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                         {role.id}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div className="flex items-center">
                           <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                             <Shield className="w-4 h-4 text-blue-600" />
                           </div>
                           <div className="text-sm font-medium text-gray-900">{role.name}</div>
                         </div>
                       </td>
                                               <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {role.created_at ? new Date(role.created_at).toLocaleDateString('vi-VN') : 'Không có'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {role.updated_at ? new Date(role.updated_at).toLocaleDateString('vi-VN') : 'Không có'}
                        </td>
                       <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                         <div className="flex items-center justify-end space-x-2">
                           <button
                             onClick={() => handleViewRole(role)}
                             className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                             title="Xem chi tiết"
                           >
                             <Eye className="w-4 h-4" />
                           </button>
                           <button
                             onClick={() => handleEditRoleClick(role)}
                             className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                             title="Sửa role"
                           >
                             <Edit className="w-4 h-4" />
                           </button>
                           {/* Tạm thời ẩn status toggle vì backend chưa có trường status */}
                           {/* <button
                             onClick={() => handleToggleRoleStatus(role.id)}
                             className={`p-1 transition-colors ${
                               role.status === 'active' 
                                 ? 'text-gray-400 hover:text-red-600' 
                                 : 'text-gray-400 hover:text-green-600'
                             }`}
                             title={role.status === 'active' ? 'Khóa role' : 'Mở khóa role'}
                           >
                             {role.status === 'active' ? (
                               <Lock className="w-4 h-4" />
                             ) : (
                               <Unlock className="w-4 h-4" />
                             )}
                           </button> */}
                         </div>
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
          </>
        )}
      </div>

      {/* Add Role Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Thêm Role Mới</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên vai trò *
                </label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập tên vai trò..."
                />
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleAddRole}
                className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Thêm vai trò
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && editingRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Sửa Role</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên Role *
                </label>
                <input
                  type="text"
                  value={editingRole.name}
                  onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập tên role..."
                />
              </div>

              {/* Tạm thời ẩn description field vì backend chưa hỗ trợ */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mô tả
                </label>
                <textarea
                  value={editingRole.description}
                  onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập mô tả role..."
                />
              </div> */}

              {/* Tạm thời ẩn status field vì backend chưa có trường status */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trạng thái
                </label>
                <select
                  value={editingRole.status}
                  onChange={(e) => setEditingRole({ ...editingRole, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Hoạt động</option>
                  <option value="inactive">Đã khóa</option>
                </select>
              </div> */}
            </div>

            <div className="p-6 border-t border-gray-200 flex space-x-3">
              <button
                onClick={handleEditRole}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4 mr-2 inline" />
                Cập nhật
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Detail Modal */}
      {showDetailModal && selectedRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Chi tiết Role</h3>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Thông tin Role</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">Tên Role:</span>
                      <p className="text-sm font-medium text-gray-900">{selectedRole.name}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">ID:</span>
                      <p className="text-sm font-medium text-gray-900">{selectedRole.id}</p>
                    </div>
                  </div>
                </div>

                    <div>
                      <span className="text-sm text-gray-600">Ngày tạo:</span>
                      <p className="text-sm text-gray-900">{selectedRole.created_at ? new Date(selectedRole.created_at).toLocaleDateString('vi-VN') : 'Không có'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Cập nhật cuối:</span>
                      <p className="text-sm text-gray-900">{selectedRole.updated_at ? new Date(selectedRole.updated_at).toLocaleDateString('vi-VN') : 'Không có'}</p>
                    </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Quyền hạn ({selectedRole.permissions?.length || 0})</h4>
                <div className="space-y-2">
                  {selectedRole.permissions && selectedRole.permissions.length > 0 ? (
                    selectedRole.permissions.map((permission) => (
                      <div key={permission} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-gray-700">{permission}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">Không có quyền hạn nào</p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleList; 
